import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import { eq } from 'tio-ui/utilities';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import {
  PhArrowLeft,
  PhPath,
  PhRocketLaunch,
  PhCheckCircle,
  PhCircle,
  PhCircleDashed,
  PhCaretDown,
  PhCaretUp,
  PhCertificate,
  PhBriefcase,
  PhClock,
  PhCalendar,
  PhLightbulb,
  PhStar,
  PhSparkle,
  PhTrendUp,
  PhFlag,
  PhArrowRight,
  PhInfo,
  PhBookOpen,
} from 'ember-phosphor-icons';
import PageHero from 'tio-ui/components/page-hero';

// Unified milestone type with all optional properties
interface RoadmapMilestone {
  id: string;
  name: string;
  type: 'certification' | 'course' | 'experience' | 'project';
  status: 'completed' | 'in-progress' | 'not-started';
  description: string;
  taEligible: boolean;
  // Completed milestones
  completedDate?: string;
  // In-progress or not-started milestones
  progress?: number;
  dueDate?: string;
  // Certification/course milestones
  costCovered?: number;
  provider?: string;
}

interface RoadmapPhase {
  id: string;
  name: string;
  description: string;
  duration: string;
  startMonth: string;
  status: 'completed' | 'in-progress' | 'not-started';
  progress: number;
  milestones: RoadmapMilestone[];
}

// Roadmap phases for Nurse Manager role
const HEALTHCARE_ROADMAP_PHASES: RoadmapPhase[] = [
  {
    id: 'phase-1',
    name: 'Foundation Building',
    description: 'Build core leadership and management skills',
    duration: '6 months',
    startMonth: 'Current',
    status: 'in-progress' as const,
    progress: 35,
    milestones: [
      {
        id: 'm1-1',
        name: 'Complete Leadership Fundamentals Certificate',
        type: 'certification' as const,
        status: 'completed' as const,
        completedDate: 'Oct 2024',
        taEligible: true,
        costCovered: 2400,
        provider: 'Coursera',
        description: 'Essential leadership principles, communication, and team dynamics',
      },
      {
        id: 'm1-2',
        name: 'Healthcare Budget Management Course',
        type: 'course' as const,
        status: 'in-progress' as const,
        progress: 60,
        dueDate: 'Jan 2025',
        taEligible: true,
        costCovered: 850,
        provider: 'ACHE',
        description: 'Financial planning, budget creation, and cost management in healthcare',
      },
      {
        id: 'm1-3',
        name: 'Shadow Current Nurse Managers (20 hours)',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Feb 2025',
        taEligible: false,
        description: 'Observational learning from experienced managers in different units',
      },
    ],
  },
  {
    id: 'phase-2',
    name: 'Skill Advancement',
    description: 'Develop advanced management competencies',
    duration: '8 months',
    startMonth: 'Month 7',
    status: 'not-started' as const,
    progress: 0,
    milestones: [
      {
        id: 'm2-1',
        name: 'Charge Nurse Role Assignment',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Mar 2025',
        taEligible: false,
        description: 'Take on shift leadership responsibilities 2-3 times per week',
      },
      {
        id: 'm2-2',
        name: 'AONE Nurse Manager Certificate Program',
        type: 'certification' as const,
        status: 'not-started' as const,
        dueDate: 'Jun 2025',
        taEligible: true,
        costCovered: 3200,
        provider: 'AONE',
        description: 'Comprehensive nurse leadership program covering all management domains',
      },
      {
        id: 'm2-3',
        name: 'Quality Improvement Project Lead',
        type: 'project' as const,
        status: 'not-started' as const,
        dueDate: 'Sep 2025',
        taEligible: false,
        description: 'Lead a unit-based QI project using Lean/Six Sigma principles',
      },
      {
        id: 'm2-4',
        name: 'Complete BSN to MSN Bridge Course',
        type: 'course' as const,
        status: 'not-started' as const,
        dueDate: 'Nov 2025',
        taEligible: true,
        costCovered: 4500,
        provider: 'WGU',
        description: 'First course toward MSN in Nursing Leadership',
      },
    ],
  },
  {
    id: 'phase-3',
    name: 'Leadership Readiness',
    description: 'Prepare for management role transition',
    duration: '4 months',
    startMonth: 'Month 15',
    status: 'not-started' as const,
    progress: 0,
    milestones: [
      {
        id: 'm3-1',
        name: 'Interview Skills & Resume Preparation',
        type: 'course' as const,
        status: 'not-started' as const,
        dueDate: 'Dec 2025',
        taEligible: false,
        provider: 'HR Learning Center',
        description: 'Prepare for internal management positions with HR coaching',
      },
      {
        id: 'm3-2',
        name: 'Apply for Nurse Manager Positions',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Feb 2026',
        taEligible: false,
        description: 'Submit applications for open Nurse Manager roles',
      },
      {
        id: 'm3-3',
        name: 'Complete Management Onboarding',
        type: 'certification' as const,
        status: 'not-started' as const,
        dueDate: 'Apr 2026',
        taEligible: false,
        provider: 'Internal',
        description: 'Complete new manager orientation and mentorship program',
      },
    ],
  },
];

// Summary stats
const HEALTHCARE_ROADMAP_SUMMARY = {
  totalMilestones: 10,
  completedMilestones: 1,
  inProgressMilestones: 1,
  totalTaCoverage: 10950,
  usedTaCoverage: 3250,
  estimatedCompletion: 'April 2026',
  monthsRemaining: 18,
};

const HEALTHCARE_TARGET_ROLE = {
  title: 'Nurse Manager',
  family: 'Nursing',
  currentMatchScore: 68,
  projectedMatchScore: 95,
  salaryIncrease: '+$20,000/year',
};

const UPS_ROADMAP_PHASES: RoadmapPhase[] = [
  {
    id: 'phase-1',
    name: 'Leadership Foundations',
    description: 'Build core shift leadership and safety skills',
    duration: '5 months',
    startMonth: 'Current',
    status: 'in-progress' as const,
    progress: 40,
    milestones: [
      {
        id: 'm1-1',
        name: 'Supervisor Leadership Program',
        type: 'certification' as const,
        status: 'completed' as const,
        completedDate: 'Oct 2024',
        taEligible: true,
        costCovered: 1800,
        provider: 'Internal',
        description: 'Leadership fundamentals, coaching, and shift planning',
      },
      {
        id: 'm1-2',
        name: 'Safety & Compliance Training',
        type: 'course' as const,
        status: 'in-progress' as const,
        progress: 50,
        dueDate: 'Jan 2025',
        taEligible: true,
        costCovered: 600,
        provider: 'OSHA',
        description: 'Workplace safety and compliance standards',
      },
      {
        id: 'm1-3',
        name: 'Shadow Hub Supervisor (16 hours)',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Feb 2025',
        taEligible: false,
        description: 'Observe shift leadership and staffing decisions',
      },
    ],
  },
  {
    id: 'phase-2',
    name: 'Operational Excellence',
    description: 'Develop scheduling and process improvement expertise',
    duration: '7 months',
    startMonth: 'Month 6',
    status: 'not-started' as const,
    progress: 0,
    milestones: [
      {
        id: 'm2-1',
        name: 'Lead Peak Season Shift',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Apr 2025',
        taEligible: false,
        description: 'Own staffing and throughput targets for a peak shift',
      },
      {
        id: 'm2-2',
        name: 'Lean Operations Certificate',
        type: 'certification' as const,
        status: 'not-started' as const,
        dueDate: 'Jul 2025',
        taEligible: true,
        costCovered: 2100,
        provider: 'Partner University',
        description: 'Continuous improvement and waste reduction practices',
      },
      {
        id: 'm2-3',
        name: 'Scheduling & Labor Planning Workshop',
        type: 'course' as const,
        status: 'not-started' as const,
        dueDate: 'Sep 2025',
        taEligible: true,
        costCovered: 750,
        provider: 'Internal',
        description: 'Forecasting, staffing, and coverage optimization',
      },
    ],
  },
  {
    id: 'phase-3',
    name: 'Promotion Readiness',
    description: 'Prepare for Hub Supervisor transition',
    duration: '4 months',
    startMonth: 'Month 13',
    status: 'not-started' as const,
    progress: 0,
    milestones: [
      {
        id: 'm3-1',
        name: 'Interview & Resume Preparation',
        type: 'course' as const,
        status: 'not-started' as const,
        dueDate: 'Nov 2025',
        taEligible: false,
        provider: 'HR Learning Center',
        description: 'Prepare for internal leadership interviews',
      },
      {
        id: 'm3-2',
        name: 'Apply for Hub Supervisor Roles',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Jan 2026',
        taEligible: false,
        description: 'Submit applications for open Hub Supervisor roles',
      },
      {
        id: 'm3-3',
        name: 'Supervisor Onboarding',
        type: 'certification' as const,
        status: 'not-started' as const,
        dueDate: 'Mar 2026',
        taEligible: false,
        provider: 'Internal',
        description: 'Complete onboarding and mentorship program',
      },
    ],
  },
];

const UPS_ROADMAP_SUMMARY = {
  totalMilestones: 9,
  completedMilestones: 1,
  inProgressMilestones: 1,
  totalTaCoverage: 5250,
  usedTaCoverage: 2400,
  estimatedCompletion: 'March 2026',
  monthsRemaining: 16,
};

const UPS_TARGET_ROLE = {
  title: 'Hub Supervisor',
  family: 'Operations Leadership',
  currentMatchScore: 62,
  projectedMatchScore: 90,
  salaryIncrease: '+$15,000/year',
};

const STARBUCKS_ROADMAP_PHASES: RoadmapPhase[] = [
  {
    id: 'phase-1',
    name: 'Leadership Foundations',
    description: 'Build core store leadership and customer experience skills',
    duration: '6 months',
    startMonth: 'Current',
    status: 'in-progress' as const,
    progress: 45,
    milestones: [
      {
        id: 'm1-1',
        name: 'Store Manager Development',
        type: 'certification' as const,
        status: 'completed' as const,
        completedDate: 'Oct 2024',
        taEligible: true,
        costCovered: 2000,
        provider: 'Internal',
        description: 'Leadership, operations, and coaching fundamentals',
      },
      {
        id: 'm1-2',
        name: 'Retail Operations Certificate',
        type: 'course' as const,
        status: 'in-progress' as const,
        progress: 55,
        dueDate: 'Jan 2025',
        taEligible: true,
        costCovered: 1200,
        provider: 'Partner University',
        description: 'Store operations and inventory management',
      },
      {
        id: 'm1-3',
        name: 'Shadow High-Volume Store Manager',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Feb 2025',
        taEligible: false,
        description: 'Observe scheduling, coaching, and daily ops',
      },
    ],
  },
  {
    id: 'phase-2',
    name: 'Operational Excellence',
    description: 'Develop labor planning and financial management skills',
    duration: '7 months',
    startMonth: 'Month 7',
    status: 'not-started' as const,
    progress: 0,
    milestones: [
      {
        id: 'm2-1',
        name: 'Labor Planning Workshop',
        type: 'course' as const,
        status: 'not-started' as const,
        dueDate: 'Apr 2025',
        taEligible: true,
        costCovered: 700,
        provider: 'Internal',
        description: 'Scheduling, labor targets, and coverage optimization',
      },
      {
        id: 'm2-2',
        name: 'Customer Experience Leadership',
        type: 'course' as const,
        status: 'not-started' as const,
        dueDate: 'Jul 2025',
        taEligible: true,
        costCovered: 900,
        provider: 'Internal',
        description: 'Drive customer connection and service standards',
      },
      {
        id: 'm2-3',
        name: 'Lead a Store Ops Project',
        type: 'project' as const,
        status: 'not-started' as const,
        dueDate: 'Sep 2025',
        taEligible: false,
        description: 'Improve throughput or inventory accuracy',
      },
    ],
  },
  {
    id: 'phase-3',
    name: 'Promotion Readiness',
    description: 'Prepare for Store Manager transition',
    duration: '4 months',
    startMonth: 'Month 14',
    status: 'not-started' as const,
    progress: 0,
    milestones: [
      {
        id: 'm3-1',
        name: 'Interview & Resume Preparation',
        type: 'course' as const,
        status: 'not-started' as const,
        dueDate: 'Nov 2025',
        taEligible: false,
        provider: 'Partner HR',
        description: 'Prepare for internal management interviews',
      },
      {
        id: 'm3-2',
        name: 'Apply for Store Manager Roles',
        type: 'experience' as const,
        status: 'not-started' as const,
        dueDate: 'Jan 2026',
        taEligible: false,
        description: 'Submit applications for open Store Manager roles',
      },
      {
        id: 'm3-3',
        name: 'Store Manager Onboarding',
        type: 'certification' as const,
        status: 'not-started' as const,
        dueDate: 'Mar 2026',
        taEligible: false,
        provider: 'Internal',
        description: 'Complete onboarding and mentorship program',
      },
    ],
  },
];

const STARBUCKS_ROADMAP_SUMMARY = {
  totalMilestones: 9,
  completedMilestones: 1,
  inProgressMilestones: 1,
  totalTaCoverage: 4800,
  usedTaCoverage: 3200,
  estimatedCompletion: 'March 2026',
  monthsRemaining: 17,
};

const STARBUCKS_TARGET_ROLE = {
  title: 'Store Manager',
  family: 'Store Leadership',
  currentMatchScore: 70,
  projectedMatchScore: 93,
  salaryIncrease: '+$12,000/year',
};

export default class CareerPathwaysPlanRoadmap extends Component {
  @tracked expandedPhases: string[] = ['phase-1'];
  @tracked selectedMilestone: string | null = null;

  get roadmapData() {
    const clientId = getCurrentDemoClientData().client.id;

    if (clientId === 'ups') {
      return {
        phases: UPS_ROADMAP_PHASES,
        summary: UPS_ROADMAP_SUMMARY,
        targetRole: UPS_TARGET_ROLE,
      };
    }

    if (clientId === 'starbucks') {
      return {
        phases: STARBUCKS_ROADMAP_PHASES,
        summary: STARBUCKS_ROADMAP_SUMMARY,
        targetRole: STARBUCKS_TARGET_ROLE,
      };
    }

    return {
      phases: HEALTHCARE_ROADMAP_PHASES,
      summary: HEALTHCARE_ROADMAP_SUMMARY,
      targetRole: HEALTHCARE_TARGET_ROLE,
    };
  }

  get phases() {
    return this.roadmapData.phases;
  }

  get summary() {
    return this.roadmapData.summary;
  }

  get targetRole() {
    return this.roadmapData.targetRole;
  }

  isPhaseExpanded = (phaseId: string) => {
    return this.expandedPhases.includes(phaseId);
  };

  togglePhase = (phaseId: string) => {
    if (this.expandedPhases.includes(phaseId)) {
      this.expandedPhases = this.expandedPhases.filter((id) => id !== phaseId);
    } else {
      this.expandedPhases = [...this.expandedPhases, phaseId];
    }
  };

  toggleMilestoneDetail = (milestoneId: string) => {
    this.selectedMilestone = this.selectedMilestone === milestoneId ? null : milestoneId;
  };

  getPhaseStatusColor = (status: string) => {
    // Solid-filled status dot (bg + white glyph); the circle has no border
    // width, so only the background intent is applied — no border classes.
    switch (status) {
      case 'completed':
        return 'tio-bg-success';
      case 'in-progress':
        return 'tio-bg-brand-accent';
      default:
        return 'bg-gray-300';
    }
  };

  getMilestoneIcon = (type: string) => {
    switch (type) {
      case 'certification':
        return PhCertificate;
      case 'course':
        return PhBookOpen;
      case 'experience':
        return PhBriefcase;
      case 'project':
        return PhLightbulb;
      default:
        return PhCircle;
    }
  };

  getMilestoneStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return PhCheckCircle;
      case 'in-progress':
        return PhCircleDashed;
      default:
        return PhCircle;
    }
  };

  getMilestoneStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'tio-fg-success tio-bg-success-soft';
      case 'in-progress':
        return 'tio-fg-brand tio-bg-brand-soft';
      default:
        return 'tio-fg-muted tio-bg-sunken';
    }
  };

  getTypeLabel = (type: string) => {
    switch (type) {
      case 'certification':
        return 'Certificate';
      case 'course':
        return 'Course';
      case 'experience':
        return 'Experience';
      case 'project':
        return 'Project';
      default:
        return type;
    }
  };

  progressStyle = (progress: number) => {
    return htmlSafe(`width: ${progress}%`);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.plan"
          class="tio-inline-row gap-2 tio-fg-brand hover:tio-fg-brand font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to My Career Plan
        </LinkTo>
      </div>

      {{! Header with Target Role }}
      <PageHero @gradient="brand">
        <:default>
          <div class="tio-col-extra-loose md:flex-row md:items-center md:justify-between">
            <div>
              <div class="tio-row mb-2">
                <PhPath @size={{32}} />
                <Text @weight="medium" class="text-violet-200">Your Development Roadmap</Text>
              </div>
              <Heading @level={{1}} class="tio-fg-inverse mb-2">
                Path to
                {{this.targetRole.title}}
              </Heading>
              <div class="tio-row-loose flex-wrap text-violet-200">
                <span class="tio-row-extra-tight">
                  <PhCalendar @size={{16}} />
                  Est. completion:
                  {{this.summary.estimatedCompletion}}
                </span>
                <span class="tio-row-extra-tight">
                  <PhClock @size={{16}} />
                  {{this.summary.monthsRemaining}}
                  months remaining
                </span>
              </div>
            </div>
            <div class="tio-col">
              {{! Match Score Progress }}
              <div class="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
                <div class="tio-row-loose">
                  <div class="text-center">
                    <div class="tio-text-3xl-bold">{{this.targetRole.currentMatchScore}}%</div>
                    <Text @size="xs" class="text-violet-200">Current</Text>
                  </div>
                  <PhArrowRight @size={{24}} class="text-violet-200" />
                  <div class="text-center">
                    <div
                      class="tio-text-3xl-bold text-green-300"
                    >{{this.targetRole.projectedMatchScore}}%</div>
                    <Text @size="xs" class="text-violet-200">After Plan</Text>
                  </div>
                </div>
              </div>
              <div class="tio-row-tight text-green-300">
                <PhTrendUp @size={{18}} />
                <span class="font-semibold">{{this.targetRole.salaryIncrease}}</span>
                <span class="text-violet-200">potential</span>
              </div>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Progress Summary Cards }}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="tio-bg-surface rounded-lg shadow-sm p-4 text-center">
          <div class="tio-text-3xl-bold tio-fg-brand">
            {{this.summary.completedMilestones}}/{{this.summary.totalMilestones}}
          </div>
          <Text @variant="muted" @size="sm">Milestones Complete</Text>
        </div>
        <div class="tio-bg-surface rounded-lg shadow-sm p-4 text-center">
          <div class="tio-text-3xl-bold tio-fg-success">
            ${{this.summary.usedTaCoverage}}
          </div>
          <Text @variant="muted" @size="sm">TA Used</Text>
        </div>
        <div class="tio-bg-surface rounded-lg shadow-sm p-4 text-center">
          <div class="tio-text-3xl-bold tio-fg-info">
            ${{this.summary.totalTaCoverage}}
          </div>
          <Text @variant="muted" @size="sm">Total TA Coverage</Text>
        </div>
        <div class="tio-bg-surface rounded-lg shadow-sm p-4 text-center">
          <div class="tio-text-3xl-bold tio-fg-warning">
            {{this.summary.inProgressMilestones}}
          </div>
          <Text @variant="muted" @size="sm">In Progress</Text>
        </div>
      </div>

      {{! Roadmap Visualization }}
      <div class="space-y-4">
        {{#each this.phases as |phase index|}}
          <div class="tio-bg-surface rounded-xl shadow-sm overflow-hidden">
            {{! Phase Header }}
            <button
              type="button"
              class="w-full p-6 text-left tio-row-between hover:tio-bg-sunken transition-colors"
              {{on "click" (fn this.togglePhase phase.id)}}
            >
              <div class="tio-row-loose">
                {{! Phase Number Circle }}
                <div
                  class="w-12 h-12 rounded-full tio-row-center text-white font-bold text-lg
                    {{this.getPhaseStatusColor phase.status}}"
                >
                  {{#if (eq phase.status "completed")}}
                    <PhCheckCircle @size={{24}} />
                  {{else}}
                    {{index}}
                  {{/if}}
                </div>
                <div>
                  <div class="tio-row">
                    <Heading @level={{3}}>{{phase.name}}</Heading>
                    {{#if (eq phase.status "in-progress")}}
                      <Chip @intent="primary" @size="sm">In Progress</Chip>
                    {{else if (eq phase.status "completed")}}
                      <Chip @intent="success" @size="sm">Complete</Chip>
                    {{else}}
                      <Chip @intent="default" @size="sm">Upcoming</Chip>
                    {{/if}}
                  </div>
                  <Text @variant="muted" @size="sm">{{phase.description}}</Text>
                  <div class="tio-row-loose mt-1 text-sm tio-fg-muted">
                    <span class="tio-row-extra-tight">
                      <PhClock @size={{14}} />
                      {{phase.duration}}
                    </span>
                    <span class="tio-row-extra-tight">
                      <PhCalendar @size={{14}} />
                      Start:
                      {{phase.startMonth}}
                    </span>
                  </div>
                </div>
              </div>
              <div class="tio-row-loose">
                {{! Progress bar }}
                {{#if (eq phase.status "in-progress")}}
                  <div class="text-right mr-4">
                    <div class="tio-text-sm-semibold tio-fg-brand">{{phase.progress}}%</div>
                    <div class="w-24 h-2 tio-bg-track rounded-full overflow-hidden mt-1">
                      <div
                        class="h-full tio-bg-brand-accent rounded-full"
                        style={{this.progressStyle phase.progress}}
                      ></div>
                    </div>
                  </div>
                {{/if}}
                {{#if (this.isPhaseExpanded phase.id)}}
                  <PhCaretUp @size={{24}} class="tio-fg-disabled" />
                {{else}}
                  <PhCaretDown @size={{24}} class="tio-fg-disabled" />
                {{/if}}
              </div>
            </button>

            {{! Phase Content (Milestones) }}
            {{#if (this.isPhaseExpanded phase.id)}}
              <div class="border-t tio-border-subtle tio-bg-sunken p-6">
                <div class="relative">
                  {{! Timeline line }}
                  <div class="absolute left-6 top-0 bottom-0 w-0.5 tio-bg-divider"></div>

                  <div class="space-y-6">
                    {{#each phase.milestones as |milestone|}}
                      <div class="relative pl-14">
                        {{! Timeline dot }}
                        <div
                          class="absolute left-4 top-2 w-5 h-5 rounded-full border-2 border-white
                            {{if
                              (eq milestone.status 'completed')
                              'tio-bg-success'
                              (if
                                (eq milestone.status 'in-progress')
                                'tio-bg-brand-accent'
                                'bg-gray-300'
                              )
                            }}"
                        ></div>

                        <div
                          class="tio-bg-surface rounded-lg border tio-border-default overflow-hidden"
                        >
                          {{! Milestone Header }}
                          <button
                            type="button"
                            class="w-full p-4 text-left hover:tio-bg-sunken transition-colors"
                            {{on "click" (fn this.toggleMilestoneDetail milestone.id)}}
                          >
                            <div class="tio-row-start-between gap-4">
                              <div class="tio-row-top tio-fill-min">
                                {{#let (this.getMilestoneIcon milestone.type) as |Icon|}}
                                  <div
                                    class="w-10 h-10 rounded-lg tio-row-center shrink-0
                                      {{this.getMilestoneStatusColor milestone.status}}"
                                  >
                                    <Icon @size={{20}} />
                                  </div>
                                {{/let}}
                                <div class="tio-fill-min">
                                  <div class="tio-row-tight flex-wrap">
                                    <Heading @level={{4}}>{{milestone.name}}</Heading>
                                  </div>
                                  <div class="tio-row-tight flex-wrap mt-1">
                                    <Chip @size="sm" @intent="default">{{this.getTypeLabel
                                        milestone.type
                                      }}</Chip>
                                    {{#if milestone.taEligible}}
                                      <Chip @size="sm" @intent="success">
                                        <span class="tio-row-extra-tight">
                                          <PhCheckCircle @size={{12}} />
                                          TA Eligible
                                        </span>
                                      </Chip>
                                    {{/if}}
                                    {{#if milestone.provider}}
                                      <span
                                        class="text-sm tio-fg-muted"
                                      >{{milestone.provider}}</span>
                                    {{/if}}
                                  </div>
                                </div>
                              </div>
                              <div class="tio-stack-end gap-1 shrink-0">
                                {{#if (eq milestone.status "completed")}}
                                  <span class="tio-fg-success tio-text-sm-medium">✓
                                    {{milestone.completedDate}}</span>
                                {{else if (eq milestone.status "in-progress")}}
                                  <span
                                    class="tio-fg-brand tio-text-sm-medium"
                                  >{{milestone.progress}}%</span>
                                {{else}}
                                  <span class="tio-fg-muted text-sm">Due:
                                    {{milestone.dueDate}}</span>
                                {{/if}}
                                {{#if milestone.costCovered}}
                                  <span
                                    class="tio-fg-success tio-text-sm-medium"
                                  >${{milestone.costCovered}} covered</span>
                                {{/if}}
                              </div>
                            </div>
                          </button>

                          {{! Expanded Detail }}
                          {{#if (eq this.selectedMilestone milestone.id)}}
                            <div class="border-t tio-border-subtle p-4 tio-bg-sunken">
                              <Text
                                @size="sm"
                                class="tio-fg-secondary mb-4"
                              >{{milestone.description}}</Text>
                              <div class="tio-flex-tight">
                                {{#if (eq milestone.status "not-started")}}
                                  <Button
                                    @intent="primary"
                                    @size="sm"
                                    @icon={{PhRocketLaunch}}
                                    @label="Start This"
                                  />
                                {{else if (eq milestone.status "in-progress")}}
                                  <Button
                                    @intent="primary"
                                    @size="sm"
                                    @icon={{PhArrowRight}}
                                    @label="Continue"
                                  />
                                {{/if}}
                                <Button
                                  @intent="default"
                                  @appearance="outlined"
                                  @size="sm"
                                  @icon={{PhInfo}}
                                  @label="View Details"
                                />
                              </div>
                            </div>
                          {{/if}}
                        </div>
                      </div>
                    {{/each}}
                  </div>
                </div>
              </div>
            {{/if}}
          </div>
        {{/each}}
      </div>

      {{! Goal/Finish Line }}
      <div class="mt-8 relative">
        <div class="tio-row-flush absolute inset-0">
          <div class="w-full border-t-2 border-dashed tio-border-success"></div>
        </div>
        <div class="relative tio-flex-center">
          <div
            class="tio-gradient-success tio-fg-inverse px-8 py-4 rounded-xl shadow-lg tio-row-loose"
          >
            <PhFlag @size={{28}} />
            <div>
              <Text @size="lg" @weight="bold">{{this.targetRole.title}}</Text>
              <Text @size="sm" class="text-green-100">Estimated:
                {{this.summary.estimatedCompletion}}</Text>
            </div>
            <PhStar @size={{28}} class="text-yellow-300" />
          </div>
        </div>
      </div>

      {{! AI Advisor CTA }}
      <div class="mt-8 tio-bg-brand-wash rounded-xl p-6 border tio-border-brand">
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div class="tio-row-top-loose">
            <div class="w-12 h-12 rounded-full tio-bg-brand-soft tio-row-center shrink-0">
              <PhSparkle @size={{24}} class="tio-fg-brand" />
            </div>
            <div>
              <Heading @level={{3}} class="tio-fg-brand">Need help adjusting your plan?</Heading>
              <Text @variant="muted">
                The AI Advisor can suggest alternative paths, recommend additional courses, or help
                reprioritize milestones based on your schedule.
              </Text>
            </div>
          </div>
          <LinkTo
            @route="authenticated.reimagined.advisor"
            class="tio-inline-row gap-2 px-5 py-3 tio-bg-brand hover:tio-bg-brand-hover tio-fg-inverse rounded-lg font-medium transition-colors whitespace-nowrap"
          >
            <PhSparkle @size={{18}} />
            Ask AI Advisor
          </LinkTo>
        </div>
      </div>
    </VStack>
  </template>
}
